import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { ApplicantDetailsResponseDto } from "../../lib/interfaces/applicant";
import { Button } from "../../ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/components/ui/dialog";
import { Textarea } from "../../ui/components/ui/textarea";

const WhatsAppMessageDialog = ({
  item,
  isOpen,
  onClose,
}: {
  item: ApplicantDetailsResponseDto;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { apiRequest } = useApiRequest();
  const { user } = useUser();
  const [text, setText] = useState("");
  const [isSending, setIsSending] = useState(false);

  const jobOffer = item.jobOfferTitle?.trim() || "die ausgeschriebene Stelle";
  // Anrede aus der Orga-Einstellung (BE baut die reale Nachricht mit derselben Anrede).
  const formal = user?.addressingMode === "SIE";
  const greeting = formal ? "Hallo" : "Hey";
  const possessive = formal ? "Ihrer" : "deiner";
  const closing = formal
    ? "Antworten Sie einfach direkt hier im Chat. Wir freuen uns, von Ihnen zu hören 😊"
    : "Antworte einfach direkt hier im Chat. Wir freuen uns, von dir zu hören 😊";

  const send = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setIsSending(true);
    // Body ist nur der Freitext; Gruß/Anrede/Abschluss baut der Provider serverseitig drumherum.
    const response = await apiRequest(`applicants/${item._id}/whatsapp-message`, "POST", {
      body: { text: trimmed },
      toast: { toastText: "WhatsApp-Nachricht gesendet" },
    });
    setIsSending(false);
    if (!response.error) {
      setText("");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>WhatsApp-Nachricht senden</DialogTitle>
          <DialogDescription>
            Nur dein Freitext wird gesendet — Gruß, Anrede und Abschluss ergänzt der Versand automatisch.
          </DialogDescription>
        </DialogHeader>

        {/* Vorschau der WhatsApp-Nachricht — nur der Freitext ist editierbar */}
        <div className="rounded-2xl border border-[#cdeed8] bg-[#e7f9ee] p-4 text-sm leading-relaxed text-slate-800">
          <p>{greeting} {item.name?.trim() || "…"} 👋</p>
          <p className="mt-3">wir melden uns zu {possessive} Bewerbung als {jobOffer}.</p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.replace(/\n/g, " "))}
            placeholder="Dein Freitext (der mittlere Teil der Nachricht) …"
            rows={3}
            className="mt-3 resize-none bg-white"
            disabled={isSending}
          />
          <p className="mt-3">{closing}</p>
        </div>
        <p className="-mt-1 text-xs text-slate-400">Keine Zeilenumbrüche — diese werden beim Senden entfernt.</p>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSending}>
            Abbrechen
          </Button>
          <Button type="button" className="gap-2" onClick={send} disabled={!text.trim() || isSending}>
            {isSending && <Loader2 className="h-4 w-4 animate-spin" />}
            Senden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsAppMessageDialog;
